<template>
    <LoadingCard v-if="loading" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>{{ logId ? 'Update' : 'Add a'}} spray diary entry
                <small class="pull-right" v-if="singleBlock">
                    <strong>Block:</strong> &ldquo;{{ singleBlock.name }}&rdquo; ({{ singleBlock.variety.name }})
                </small>
            </h1>

        </header>
        <form @submit.prevent="attemptCreate" ref="createForm" novalidate>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Required Details
                </div>

                <div class="card-body pt-3">

                    <!-- Blocks -->
                    <div class="form-group row" v-if="!singleBlock">
                        <label class="col-sm-2 col-form-label">Sprayed Blocks</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="selectedBlocks"
                                        :options="blocks"
                                        :hide-selected="true"
                                        placeholder="Blocks..."
                                        label="name"
                                        track-by="id"
                                        :multiple="true"
                                >
                                    <template slot="option" slot-scope="props">
                                        <div class="option__desc">{{ props.option.name }} ({{ props.option.variety.name }})</div>
                                    </template>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Spray Date -->
                    <div class="form-group row">
                        <label for="date" class="col-sm-2 col-form-label">Spray Date</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="date"
                                    v-model="date"
                                    :config="datePicker"
                                    class="form-control"
                                    :class="{'is-invalid': errors.has('date')}"
                                    placeholder="Select date"
                                    v-validate="validation.date"
                                    @on-change="updateFinishTime"
                                    name="date" />
                            <i class="form-group__bar"></i>
                            <div class="invalid-feedback" v-if="errors.has('date')">{{ errors.first('date') }}</div>
                        </div>
                    </div>

                    <!-- Applicator -->
                    <div class="form-group row" v-if="growsafe_number">
                        <label class="col-sm-2 col-form-label">Growsafe Number</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <input type="text" readonly :value="growsafe_number" class="form-control" />
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>
                    <div class="form-group row" v-else>
                        <label class="col-sm-2 col-form-label">Applicator</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="applicator"
                                        :options="growsafeUserData"
                                        placeholder="Applicator..."
                                        label="label"
                                        track-by="id"
                                        v-validate="validation.applicator"
                                        data-vv-name="applicator_id"
                                        data-vv-as="applicator_id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Spray Volume -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Spray Volume (lt/hec)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueMask v-model.number="spray_volume" class="form-control"
                                         :class="{'is-invalid': errors.has('spray_volume')}"
                                         placeholder="0..."
                                         v-validate="validation.spray_volume"
                                         data-vv-name="spray_volume"
                                         data-vv-as="spray volume"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                                <div class="invalid-feedback" v-if="errors.has('spray_volume')">{{ errors.first('spray_volume') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Chemicals Used -->
                <div class="bg-light">
                    <h6 class="card-header text-secondary">Chemicals Used</h6>
                </div>

                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="entryRows" :fields="entriesTable.fields" :show-empty="true">
                        <template slot="chemical" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`chemical_${row.index}`) || ''"
                                        @input="setRow('chemical', row.index, $event)"
                                        :value="row.item.chemical"
                                        v-validate="validation.entry_chemical"
                                        :data-vv-name="`chemical_${row.index}`"
                                        data-vv-as="chemical"
                                        :options="sortedChemicals"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Chemical"
                                        label="name"
                                        track-by="id"
                                >
                                    <template slot="option" slot-scope="props">
                                        <div class="d-flex">
                                            <span>{{ props.option.name }}</span>
                                            <span class="ml-auto badge badge-warning" v-if="recommendedRate(props.option)"
                                                  v-b-tooltip="`Recommended Rate per 100lts`">
                                                {{ recommendedRate(props.option) | number_format }} {{ props.option.is_liquid ? 'ml' : 'gm' }}
                                            </span>
                                        </div>
                                    </template>
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.rate" class="form-control"
                                         v-b-tooltip="errors.first(`rate_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`rate_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_rate"
                                         :data-vv-name="`rate_${row.index}`"
                                         data-vv-as="rate"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="target" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        :value="row.item.target"
                                        v-b-tooltip="errors.first(`target_${row.index}`) || ''"
                                        @input="setRow('target', row.index, $event)"
                                        v-validate="validation.entry_target"
                                        :data-vv-name="`target_${row.index}`"
                                        data-vv-as="target"
                                        :options="chemicalTargets"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Target"
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="weight" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.weight" class="form-control"
                                         v-b-tooltip="errors.first(`weight_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`weight_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_weight"
                                         :data-vv-name="`weight_${row.index}`"
                                         data-vv-as="weight"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('weight', row.index, $event)" />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="recommended" slot-scope="row">
                            {{ recommendedWeight(row.item) | number_format }}
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addRow">Add Another Chemical</button>
                    </div>
                </div>

                <div class="bg-light">
                    <h6 class="card-header text-secondary">Fertilisers Used</h6>
                </div>
                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="fertiliserEntryRows" :fields="fertiliserEntriesTable.fields" :show-empty="true">
                        <template slot="fertiliser" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`fertiliser_${row.index}`) || ''"
                                        @input="setFertiliserRow('fertiliser', row.index, $event)"
                                        :value="row.item.fertiliser"
                                        v-validate="fertiliserValidation.entry_fertiliser"
                                        :data-vv-name="`fertiliser_${row.index}`"
                                        data-vv-as="fertiliser"
                                        :options="sortedFertilisers"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Fertiliser"
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.rate" class="form-control"
                                         v-b-tooltip="errors.first(`rate_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`rate_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="fertiliserValidation.entry_rate"
                                         :data-vv-name="`rate_${row.index}`"
                                         data-vv-as="rate"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="weight" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.weight" class="form-control"
                                         v-b-tooltip="errors.first(`weight_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`weight_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="fertiliserValidation.entry_weight"
                                         :data-vv-name="`weight_${row.index}`"
                                         data-vv-as="weight"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('weight', row.index, $event)" />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeFertiliserRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addFertiliserRow">Add Another Fertiliser</button>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Optional Details
                </div>

                <div class="card-body pt-3">


                    <!-- equipment -->
                    <div class="form-group row" v-if="logId">
                        <label class="col-sm-2 col-form-label">Spray Unit</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <input type="text" readonly :value="sprayUnitSummary" class="form-control" />
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>
                    <div class="form-group row" v-else>
                        <label class="col-sm-2 col-form-label">Spray Unit</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="spray_unit"
                                        :options="equipment"
                                        placeholder="Select Spray Unit..."
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <div class="invalid-feedback" v-if="errors.has('spray_unit')">{{ errors.first('spray_unit') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Time Taken -->
                    <div class="form-group row">
                        <label for="end_time" class="col-sm-2 col-form-label">Spray Finish Time</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="end_time"
                                    v-model="end_time"
                                    :config="timePicker"
                                    class="form-control"
                                    placeholder="Select time"
                                    name="end_time" />
                            <i class="form-group__bar"></i>
                            <div class="input-group-prepend" v-if="end_time && date">
                                <span class="input-group-text">Time taken: {{ time_taken }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Wind Direction -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Wind Direction</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="wind_direction"
                                        :options="windDirectionValues"
                                        :allow-empty="false"
                                        deselect-label=""
                                        placeholder="Wind Direction..."
                                        label="description"
                                        track-by="name"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Wind Speed -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Wind Speed (km/h)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="wind_speed"
                                        :options="windSpeedValues"
                                        :allow-empty="false"
                                        deselect-label=""
                                        placeholder="Wind Speed..."
                                        label="description"
                                        track-by="name"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Cloud Cover -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Cloud Cover (percentage)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueMask v-model.number="cloud_cover" class="form-control"
                                         :class="{'is-invalid': errors.has('cloud_cover')}"
                                         placeholder="0..."
                                         v-validate="validation.cloud_cover"
                                         data-vv-name="cloud_cover"
                                         data-vv-as="cloud cover"
                                         :options="{numeral: true, numeralDecimalScale: 0, numeralIntegerScale: 3}" />
                                <div class="invalid-feedback" v-if="errors.has('cloud_cover')">{{ errors.first('cloud_cover') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Temperature -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Temperature (&deg;c)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueMask v-model.number="temperature" class="form-control"
                                         :class="{'is-invalid': errors.has('temperature')}"
                                         placeholder="0..."
                                         v-validate="validation.temperature"
                                         data-vv-name="temperature"
                                         data-vv-as="temperature"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                                <div class="invalid-feedback" v-if="errors.has('temperature')">{{ errors.first('temperature') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Comments -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Comments</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <textarea class="form-control" v-model="comments" placeholder="Comments..." v-autosize></textarea>
                                <div class="invalid-feedback" v-if="errors.has('comments')">{{ errors.first('comments') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-6">
                            <button type="button" @click="attemptDelete" v-if="logId" class="btn btn-danger">Delete</button>
                        </div>
                        <div class="col-sm-6 text-right">
                            <button type="submit" class="btn btn-success">{{ logId ? 'Update' : 'Create' }}</button>
                        </div>
                    </div>

                    <div class="text-right">
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import listWindDirectionValuesQuery from '@gql/enumValues/queryWindDirectionEnumValues.graphql';
import listWindSpeedValuesQuery from '@gql/enumValues/queryWindSpeedEnumValues.graphql';
import createSprayLogMutation from '@views/SprayDiary/Create/mutationCreateSprayLog.graphql';
import deleteSprayLogMutation from '@views/SprayDiary/Create/mutationDeleteSprayLog.graphql';
import updateSprayLogMutation from '@views/SprayDiary/Create/mutationUpdateSprayLog.graphql';
import currentUserQuery from '@views/SprayDiary/Create/queryCurrentUser.graphql';
import findSprayLogQuery from '@views/SprayDiary/Create/queryFindSprayLog.graphql';
import listChemicalTargetsQuery from '@views/SprayDiary/Create/queryListChemicalTargets.graphql';
import listEquipmentQuery from '@views/SprayDiary/Create/queryListEquipment.graphql';
import listLinkedChemicalsQuery from '@views/SprayDiary/Create/queryListGrowerLinkedChemicalCommercialVariants.graphql';
import listLinkedFertilisersQuery from '@views/SprayDiary/Create/queryListGrowerLinkedFertilisers.graphql';
import listGrowsafeUsersQuery from '@views/SprayDiary/Create/queryListGrowsafeUsers.graphql';
import addMinutes from 'date-fns/add_minutes';
import differenceInMilliseconds from 'date-fns/difference_in_milliseconds';
import differenceInMinutes from 'date-fns/difference_in_minutes';
import formatDate from 'date-fns/format';

import ConfirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import DifferenceFormatter from 'humanize-duration-es6';
import differenceFormatterLocaleEn from 'humanize-duration-es6/dist/locale/en';

const datePickerConfig = (config = {}) => {
  return {
    plugins: [new ConfirmDatePlugin({ confirmIcon: '' })],
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: true,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

export default {
  name: 'CreateSprayDiary',
  props: ['blocks', 'userId', 'logId', 'cropTypeId'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    currentLog: {
      query: findSprayLogQuery,
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          this.selectedBlocks = [data.findSprayLog.block];
          this.cloud_cover = data.findSprayLog.cloud_cover;
          this.temperature = data.findSprayLog.temperature;
          this.spray_volume = data.findSprayLog.volume;
          this.spray_unit_name = data.findSprayLog.spray_unit_name;
          this.spray_unit_calibrated_on = data.findSprayLog.spray_unit_calibrated_on;
          this.growsafe_number = data.findSprayLog.growsafe_number;
          this.wind_direction = this.windDirectionValues.find(val => val.name === data.findSprayLog.wind_direction);
          this.wind_speed = this.windSpeedValues.find(val => val.name === data.findSprayLog.wind_speed);
          this.comments = data.findSprayLog.comments;
          this.entryRows = data.findSprayLog.entries.map(item => ({ ...item }));
          this.fertiliserEntryRows = data.findSprayLog.fertiliserEntries.map(item => ({ ...item }));
          this.date = data.findSprayLog.sprayed_at;
          this.end_time = formatDate(addMinutes(data.findSprayLog.sprayed_at, data.findSprayLog.time_taken), 'HH:mm');
        }
      },
      variables() {
        return {
          input: {
            id: this.logId,
          },
        };
      },
      skip() {
        return !this.logId || !this.windSpeedValues.length || !this.windDirectionValues.length;
      },
    },
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
      variables() {
        const input = {
            exclude_type: ['base'],
        };

        if (this.userId) {
          input.user_id = this.userId;
        }

        return {input};
      },
    },
    chemicalTargets: {
      query: listChemicalTargetsQuery,
      update: data => data.filterChemicalTargets.data,
    },
    growsafeUsers: {
      query: listGrowsafeUsersQuery,
      update: data => data.filterGrowsafeUsers.data,
    },
    equipment: {
      query: listEquipmentQuery,
      update: data => data.filterEquipment.data,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
      skip() {
        return !!this.logId;
      },
    },
    windSpeedValues: {
      query: listWindSpeedValuesQuery,
      update: data => data.__type.enumValues,
    },
    windDirectionValues: {
      query: listWindDirectionValuesQuery,
      update: data => data.__type.enumValues,
    },
  },
  watch: {
    currentUser() {
      if (this.currentUser && this.currentLog) {
        if (this.currentUser.role.name !== 'grower' && this.currentLog.creator_id !== this.currentUser.id) {
          console.log(this.currentLog.creator_id, this.currentUser.id);
          this.$notifyFailure('Not allowed.');
          setTimeout(() => this.$router.push({ name: 'dashboard' }), this.$redirectTimeout);
        }
      }
    },
    currentLog() {
      if (this.currentUser && this.currentLog) {
        if (this.currentUser.role.name !== 'grower' && this.currentLog.creator_id !== this.currentUser.id) {
          console.log(this.currentLog.creator_id, this.currentUser.id);
          this.$notifyFailure('Not allowed.');
          setTimeout(() => this.$router.push({ name: 'dashboard' }), this.$redirectTimeout);
        }
      }
    },
  },
  data() {
    return {
      loading: 0,
      currentUser: null,
      applicator: null,
      spray_unit: null,
      growsafeUsers: [],
      equipment: [],
      growsafe_number: null,
      spray_unit_name: null,
      spray_unit_calibrated_on: null,
      selectedBlocks: this.blocks.length === 1 ? this.blocks : [],
      date: null,
      end_time: null,
      wind_direction: null,
      wind_speed: null,
      cloud_cover: null,
      temperature: null,
      spray_volume: null,
      comments: null,
      windSpeedValues: [],
      windDirectionValues: [],
      fertiliserEntryRows: Array.from({ length: 3 }, () => ({
        fertiliser: null,
        rate: null,
        weight: null,
      })),
      entryRows: Array.from({ length: 3 }, () => ({
        chemical: null,
        rate: null,
        target: null,
        weight: null,
      })),
      linkedChemicals: [],
      linkedFertilisers: [],
      chemicalTargets: [],
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      datePicker: datePickerConfig({
        altFormat: 'M j, Y at h:iK',
        dateFormat: 'Y-m-d H:i:S',
      }),
      timePicker: datePickerConfig({
        altFormat: 'h:iK',
        dateFormat: 'H:i',
        noCalendar: true,
      }),
      fertiliserValidation: {
        entry_fertiliser: { required: true },
        entry_rate: { required: true, min_value: 0 },
        entry_weight: { required: true, min_value: 0 },
      },
      validation: {
        entry_chemical: { required: true },
        entry_target: { required: true },
        date: { required: true },
        applicator: { required: true },
        equipment: { required: true },
        entry_rate: { required: true, min_value: 0 },
        entry_weight: { required: true, min_value: 0 },
        spray_volume: { required: true, min_value: 0 },
        cloud_cover: { min_value: 0 },
        temperature: {},
      },
    };
  },
  computed: {
    entriesTable() {
      return {
        fields: [
          { key: 'chemical', label: 'Commercial Product' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'target', label: 'Target' },
          { key: 'weight', label: 'Weight(kgs or lts)' },
          this.singleBlock ? { key: 'recommended', label: 'Recommended(l/kg)' } : null,
          { key: 'remove', label: 'Remove' },
        ].filter(Boolean),
      };
    },
    growsafeUserData() {
      return this.growsafeUsers.map(user => ({
        id: user.id,
        label: `${user.first_name} ${user.last_name} (${user.certificate_number})`,
        certificate_number: user.certificate_number,
      }));
    },
    singleBlock() {
      if (this.logId) {
        return this.selectedBlocks[0];
      }
      return this.blocks.length === 1 ? this.blocks[0] : null;
    },
    time_taken_in_minutes() {
      if (!this.end_time) return null;

      return differenceInMinutes(
        `${this.date.split(' ')[0]} ${this.end_time}:00`,
        this.date,
      );
    },
    time_taken() {
      if (!this.end_time) return null;

      const formatter = new DifferenceFormatter(
        differenceFormatterLocaleEn,
      );
      return formatter.humanize(
        differenceInMilliseconds(
          this.date,
          `${this.date.split(' ')[0]} ${this.end_time}:00`,
        ),
      );
    },
    sortedChemicals() {
      return [...this.linkedChemicals].sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedFertilisers() {
      return [...this.linkedFertilisers].sort((a, b) => a.name.localeCompare(b.name));
    },
    sprayUnitSummary() {
      if (!this.spray_unit_name) {
        return null;
      }

      if (this.spray_unit_calibrated_on) {
        return `${this.spray_unit_name} calibrated on ${formatDate(this.spray_unit_calibrated_on, 'Do MMM YYYY')}`;
      }

      return this.spray_unit_name;
    },
  },
  methods: {
    recommendedWeight(row) {
      if(!this.singleBlock || !row.rate || ! this.spray_volume) {
        return null;
      }

      return (this.singleBlock.canopy_area * this.spray_volume / 100 * row.rate / 1000).toFixed(2);
    },
    recommendedRate(chemical) {
      const cropTypeId = this.cropTypeId || (this.singleBlock ? this.singleBlock.cropType.id : null);
      if (!cropTypeId) return null;

      let link = chemical.cropTypes.find(cropTypeLink => cropTypeLink.crop_type_id === cropTypeId);
      if (link) {
        return link.recommended_rate;
      }

      return null;
    },
    addRow() {
      this.entryRows.push({
        chemical: null,
        rate: null,
        target: null,
        weight: null,
      });
    },
    removeRow(index) {
      this.entryRows.splice(index, 1);
    },
    setRow(field, index, value) {
      this.entryRows[index][field] = value;
    },
    addFertiliserRow() {
      this.fertiliserEntryRows.push({
        fertiliser: null,
        rate: null,
        weight: null,
      });
    },
    removeFertiliserRow(index) {
      this.fertiliserEntryRows.splice(index, 1);
    },
    setFertiliserRow(field, index, value) {
      this.fertiliserEntryRows[index][field] = value;
    },
    updateFinishTime(_, startDate) {
      const time = (startDate.split(' ')[1] || '00:00:00').substr(0, 5);
      this.$set(this.timePicker, 'minDate', time);
    },
    attemptDelete() {
      const notification = this.$notifyConfirm('Deleting a spray diary entry cannot be undone. Are you sure you wish to delete this?', {
        buttons: [
          this.$notification.button('Yes', 'btn btn-primary', () => {
            notification.close();
            this.doDelete();
          }),
          this.$notification.button('Cancel', 'btn btn-light', () => notification.close()),
        ],
      });
    },
    doDelete() {
      this.$apollo
        .mutate({
          mutation: deleteSprayLogMutation,
          variables: { input: { id: this.logId } },
        })
        .then(() => {
          this.$notifySuccess(`Spray log deleted!`);
          this.$router.back();
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    attemptCreate() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const mutationInput = {
          sprayed_at: this.date,
          time_taken: this.time_taken_in_minutes,
          volume: this.spray_volume,
          comments: this.comments,
          wind_direction: this.wind_direction ? this.wind_direction.name : null,
          wind_speed: this.wind_speed ? this.wind_speed.name : null,
          temperature: this.temperature,
          cloud_cover: this.cloud_cover,
          entries: this.entryRows.map(row => ({
            chemical_id: row.chemical.id,
            target_id: row.target.id,
            rate: row.rate,
            weight: row.weight,
          })),
          fertiliserEntries: this.fertiliserEntryRows.map(row => ({
            fertiliser_id: row.fertiliser.id,
            rate: row.rate,
            weight: row.weight,
          })),
        };

        if (this.logId) {
          mutationInput.id = this.logId;
        } else {
          if (this.spray_unit) {
            mutationInput.spray_unit_id = this.spray_unit.id;
          }
          mutationInput.growsafe_number = this.applicator.certificate_number;
          mutationInput.block_id = this.selectedBlocks.map(block => block.id);

        }

        this.$apollo
          .mutate({
            mutation: this.logId ? updateSprayLogMutation : createSprayLogMutation,
            variables: {
              input: mutationInput,
            },
          })
          .then(() => {
            this.$notifySuccess(`Spray log ${this.logId ? 'updated' : 'created'}!`);
            if (this.singleBlock) {
              setTimeout(() => this.$router.push({ name: 'spray-diary.view', params: { blockId: this.singleBlock.id } }), this.$redirectTimeout);
            } else if (!this.logId) {
              this.date = null;
              this.end_time = null;
              this.wind_direction = null;
              this.wind_speed = null;
              this.cloud_cover = null;
              this.temperature = null;
              this.spray_volume = null;
              this.applicator = null;
              this.spray_unit = null;
              this.growsafe_number = null;
              this.comments = null;
              this.windSpeedValues = [];
              this.windDirectionValues = [];
              this.spray_unit_name = null;
              this.spray_unit_calibrated_on = null;
              this.fertiliserEntryRows = Array.from({ length: 3 }, () => ({
                fertiliser: null,
                rate: null,
                weight: null,
              }));
              this.entryRows = Array.from({ length: 3 }, () => ({
                chemical: null,
                rate: null,
                weight: null,
              }));
            }

            this.$validator.reset();

          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
  },
};
</script>
